import {
  Box,
  Fade,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  InputProps,
  Spacer,
  Text,
  Tooltip,
  useColorModeValue,
  useDisclosure,
  IconButton,
} from '@chakra-ui/react';
import _ from 'lodash';
import React from 'react';

import { colors } from '../colors';
import { Icon } from './icons';

type SearchBarInputProps = {
  query: string;
  onChangeQuery: (newQuery: string) => void;
  isDisabled?: boolean;
  placeholder?: string;
  inputProps?: InputProps;
};
export const SearchBarInput = React.forwardRef<
  HTMLInputElement,
  SearchBarInputProps
>((props, ref) => {
  const { query, onChangeQuery, isDisabled, inputProps } = props;
  const iconColor = useColorModeValue('gray.800', 'gray.400');

  return (
    <InputGroup>
      <InputLeftElement pointerEvents="none">
        <Icon color={iconColor} icon="magnifying-glass" />
      </InputLeftElement>
      <Input
        ref={ref}
        value={query}
        onChange={(e) => {
          onChangeQuery(String(e.target.value));
        }}
        isDisabled={isDisabled}
        placeholder={inputProps?.placeholder || 'Search'}
        backgroundColor={inputProps?.backgroundColor || 'white'}
        {...inputProps}
      />
      {query && (
        <InputRightElement>
          <IconButton
            aria-label="Clear search"
            icon={<Icon color={iconColor} icon="circle-xmark" />}
            onClick={() => onChangeQuery('')}
            variant="v2-ghost"
            size="sm"
          />
        </InputRightElement>
      )}
    </InputGroup>
  );
});
SearchBarInput.displayName = 'SearchBarInput';

export const SearchBar = ({
  query,
  onChangeQuery,
  isDisabled = false,
  backgroundColor,
  inputProps,
}: {
  query: string;
  onChangeQuery: (newQuery: string) => void;
  isDisabled?: boolean;
  backgroundColor?: string;
  inputProps?: InputProps;
}) => {
  return (
    <Box
      borderRadius="8px"
      padding="16px"
      width="100%"
      zIndex={1}
      color={colors.black[95]}
      backgroundColor={backgroundColor || 'white'}
    >
      <SearchBarInput
        query={query}
        onChangeQuery={onChangeQuery}
        isDisabled={isDisabled}
        inputProps={inputProps}
      />
    </Box>
  );
};

export const doesSearchQueryExistInString = ({
  queryString,
  stringToEvaluate,
}: {
  queryString: string;
  stringToEvaluate: string;
}) => {
  const searchTerm = String(queryString).toLowerCase();
  const searchTermNoSpaces = _.camelCase(searchTerm).toLowerCase();

  const lowerCaseValue = stringToEvaluate.toLowerCase();
  if (
    _.includes(lowerCaseValue, searchTerm) ||
    _.includes(lowerCaseValue, searchTermNoSpaces)
  ) {
    return true;
  }
  return false;
};

export const filterSearchRows = ({
  query,
  allRowValues,
}: {
  query: string;
  allRowValues: string[];
}): string[] => {
  const searchTerm = String(query).toLowerCase();
  const searchTermNoSpaces = _.camelCase(searchTerm).toLowerCase();

  return _.filter(allRowValues, (variable) => {
    const lowerCaseVariable = variable.toLowerCase();
    if (
      _.includes(lowerCaseVariable, searchTerm) ||
      _.includes(lowerCaseVariable, searchTermNoSpaces)
    ) {
      return true;
    }
    return false;
  });
};

export const SearchRow = ({
  rowValue,
  leftIcon,
  showHoverBackground = true,
}: {
  rowValue: string;
  leftIcon?: { iconName: string; tooltip?: string };
  showHoverBackground?: boolean;
}) => {
  const {
    isOpen: showCopiedAlert,
    onOpen: onShowAlert,
    onClose: onCloseAlert,
  } = useDisclosure();

  return (
    <Flex
      w="100%"
      alignItems="center"
      cursor="pointer"
      onClick={(e) => {
        e.stopPropagation();
        navigator.clipboard.writeText(rowValue);
        onShowAlert();

        setTimeout(onCloseAlert, 2000);
      }}
      _hover={
        showHoverBackground ? { backgroundColor: 'blackAlpha.50' } : undefined
      }
    >
      {leftIcon && (
        <Tooltip label={leftIcon.tooltip}>
          <Box alignSelf="normal">
            <Icon
              icon={leftIcon.iconName}
              color={colors.black[70]}
              style={{ marginRight: '8px' }}
            />
          </Box>
        </Tooltip>
      )}
      <Text fontSize="14px" lineHeight="28px" color={colors.black[70]} mr="8px">
        {rowValue}
      </Text>
      <Icon icon="copy" color={colors.black[70]} />
      <Spacer />
      <Fade
        in={showCopiedAlert}
        transition={{
          exit: { duration: 0.5 },
          enter: { duration: 0.5 },
        }}
      >
        <Flex
          backgroundColor={colors.aurora[400]}
          color="white"
          fontSize="12px"
          fontWeight={500}
          borderRadius="8px"
          ml="4px"
        >
          <Text padding="4px 16px">Copied!</Text>
        </Flex>
      </Fade>
    </Flex>
  );
};
